/**
 * fb-team-search.js
 * [FROST BUDDY] Search bar + conference bubble filters for the team logo grid.
 *
 * Usage: wrap the search/filter UI inside <fb-team-search> in team-logo-list.liquid.
 * The element queries its next sibling .team-logo-list__grid for filterable cards.
 */

const POWER_CONFERENCES = ['SEC', 'Big Ten', 'ACC', 'Big 12'];

class FbTeamSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.fb-team-search__input');
    this.clearBtn = this.querySelector('.fb-team-search__clear');
    this.bubblesContainer = this.querySelector('.fb-team-search__bubbles');
    this.countLabel = this.querySelector('.fb-team-search__count');
    this.emptyState = this.querySelector('.fb-team-search__empty');

    // Grid is the next sibling element
    this.grid = this.parentElement.querySelector('.team-logo-list__grid');
    if (!this.grid) return;

    // Cache card data — read conference directly from data attribute
    this.cards = [...this.grid.querySelectorAll('.team-card[data-team-title]')].map((el) => {
      const raw = el.dataset.conference || '';
      const normalized = POWER_CONFERENCES.includes(raw) ? raw : 'other';
      return { el, title: el.dataset.teamTitle, conference: normalized };
    });

    this.activeConference = 'all';
    this.searchQuery = '';

    // Debounced search
    let timer;
    this.input?.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.searchQuery = this.input.value.trim().toLowerCase();
        if (this.clearBtn) this.clearBtn.style.display = this.input.value ? 'block' : 'none';
        this.applyFilters();
      }, 150);
    });

    // Clear button
    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.searchQuery = '';
      this.clearBtn.style.display = 'none';
      this.input.focus();
      this.applyFilters();
    });

    // Bubble clicks
    this.bubblesContainer?.addEventListener('click', (e) => {
      const bubble = e.target.closest('.fb-team-search__bubble');
      if (!bubble) return;
      this.activeConference = bubble.dataset.conference;
      this.bubblesContainer.querySelectorAll('.fb-team-search__bubble').forEach((b) => {
        b.classList.toggle('fb-team-search__bubble--active', b.dataset.conference === this.activeConference);
      });
      this.applyFilters();
    });

    // Prefill from ?school= (handed off by the homepage Collabs finder, sections/fb-collabs.liquid)
    const school = new URLSearchParams(window.location.search).get('school')?.trim();
    if (school && this.input) {
      this.input.value = school;
      this.searchQuery = school.toLowerCase();
      if (this.clearBtn) this.clearBtn.style.display = 'block';
    }

    // Initial render
    this.applyFilters();

    // Auto-focus input when navigating via #find-your-team anchor
    const focusIfHash = () => {
      if (window.location.hash === '#find-your-team' && this.input) {
        setTimeout(() => this.input.focus(), 300);
      }
    };
    focusIfHash();
    window.addEventListener('hashchange', focusIfHash);
  }

  applyFilters() {
    const query = this.searchQuery;
    const conf = this.activeConference;
    let visibleCount = 0;

    // Track per-conference counts among search-matched items (using normalized keys)
    const confCounts = {};

    for (const card of this.cards) {
      const matchesSearch = !query || card.title.includes(query);
      const matchesConf = conf === 'all' || card.conference === conf;
      const visible = matchesSearch && matchesConf;

      card.el.style.display = visible ? 'flex' : 'none';
      if (visible) visibleCount++;

      // Count search-matched items per normalized conference (ignoring conference filter)
      if (matchesSearch) {
        confCounts[card.conference] = (confCounts[card.conference] || 0) + 1;
      }
    }

    // Update bubble counts
    this.bubblesContainer?.querySelectorAll('.fb-team-search__bubble').forEach((b) => {
      const countEl = b.querySelector('.fb-team-search__bubble-count');
      if (!countEl) return;
      const bConf = b.dataset.conference;
      if (bConf === 'all') {
        const total = Object.values(confCounts).reduce((s, n) => s + n, 0);
        countEl.textContent = total;
      } else if (bConf === 'other') {
        countEl.textContent = confCounts['other'] || 0;
      } else {
        // Power conference — match by exact name (bubble data-conference = "SEC", "Big Ten", etc.)
        countEl.textContent = confCounts[bConf] || 0;
      }
    });

    // Update visible count label
    if (this.countLabel) {
      this.countLabel.textContent = `${visibleCount} school${visibleCount !== 1 ? 's' : ''}`;
    }

    // Empty state
    if (this.emptyState) {
      this.emptyState.style.display = visibleCount === 0 ? '' : 'none';
    }
  }
}

customElements.define('fb-team-search', FbTeamSearch);
